<template>
  <article class="subteam-tile">
    <div class="subteam-icon-wrapper">
      <!-- Render the dynamic async component, showing nothing while loading -->
      <component
        v-if="iconComponent"
        :is="iconComponent"
        class="subteam-icon"
        :aria-label="`${data.title} icon`"
        role="img"
        :stroke-width="1.5"
      />
    </div>
    
    <div class="subteam-content">
      <h3 :id="tileId" class="subteam-title">
        <a :href="`#${tileId}`" class="header-link">{{ data.title }}</a>
      </h3>
      <p class="subteam-description">{{ data.description }}</p>
    </div>
  </article>
</template>

<script>
import { defineAsyncComponent, markRaw } from "vue";

export default {
  name: "SubteamTile",
  props: {
    data: {
      type: Object,
      required: true
    }
  },
  computed: {
    tileId() {
      return this.data.title ? this.data.title.toLowerCase().replace(/\s+/g, '-') : '';
    },
    iconComponent() {
      let key = this.data.lucide;
      
      if (!key) {
        key = "info";
      }

      // 3. Dynamically import the specific icon file on demand
      // markRaw prevents Vue from making the component reactive, saving memory
      return markRaw(
        defineAsyncComponent(() =>
          import(`../../node_modules/lucide-vue-next/dist/esm/icons/${key}.js`)
            .then((m) => m.default)
            .catch(() => {
              // Fail-safe: load CircleHelp if the specified icon doesn't exist
              return import(`../../node_modules/lucide-vue-next/dist/esm/icons/circle-help.js`)
                .then((m) => m.default);
            })
        )
      );
    }
  }
}
</script>

<style lang="scss">
.subteam-tile {
  display: grid;
  grid-template-columns: auto 1fr;
  align-items: center;
  gap: 1.5rem;
  width: 100%;
  height: 100%;
  padding: 1rem 0.5rem;
  color: var(--medium);
  margin: 0 auto;

  .subteam-icon-wrapper {
    display: flex;
    align-items: center;
    justify-content: center;
    align-self: center;

    .subteam-icon {
      width: 5.5rem;
      height: 5.5rem;
      color: var(--regular);
      stroke: currentColor;
      flex-shrink: 0;
      transition: transform var(--transition-fast, 0.2s ease-in-out);
    }
  }

  .subteam-content {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    text-align: left;

    .subteam-title {
      color: var(--text-muted);
      font-family: var(--font-h3);
      font-size: 1.25rem;
      font-weight: bold;
      line-height: 1.2;
      margin: 0 0 0.4rem 0;
      text-wrap: balance;
      word-break: normal;

      .header-link {
        color: inherit;
        text-decoration: none;
      }
    }

    .subteam-description {
      color: var(--dark);
      font-size: 0.95rem;
      line-height: 1.35;
      margin: 0;
      word-break: normal;
    }
  }

  &:hover {
    .subteam-icon {
      transform: scale(1.1);
    }
  }
}

@media screen and (max-width: 640px) {
  .subteam-tile {
    gap: 1rem;
    padding: 0.75rem 0.25rem;

    .subteam-icon-wrapper {
      .subteam-icon {
        width: clamp(3.5rem, 13vw, 4.75rem);
        height: clamp(3.5rem, 13vw, 4.75rem);
      }
    }

    .subteam-content {
      .subteam-title {
        font-size: clamp(1.05rem, 4vw, 1.15rem);
        margin-bottom: 0.3rem;
      }

      .subteam-description {
        font-size: clamp(0.85rem, 3.2vw, 0.95rem);
        line-height: 1.3;
      }
    }
  }
}
</style>